import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n";
import { createStaff, fetchStaffs, updateStaff, type StaffRow } from "@/services/staffs";

// "Accès" (Charles #7 / #12): each person uses the modules ticked here and opens on his home module.
// An admin uses every module and manages this page. The server refuses any call outside a person's
// modules — this screen only says who may do what. On his own row an admin can neither drop his admin
// right nor switch his account off (the server refuses both), so the app always keeps one admin.
export default function AccessPage() {
  const { t } = useT();
  const { staff: me } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["staffs"], queryFn: fetchStaffs });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["staffs"] });
  const modules = q.data?.modules ?? [];

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <h2 className="border-b border-slate-100 bg-slate-50 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{t("access.title")}</h2>
        <p className="px-5 pt-2 text-xs text-slate-500">{t("access.hint")}</p>
        {q.error && <p className="px-5 py-2 text-sm text-red-700">{(q.error as Error).message}</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">{t("access.person")}</th>
                {modules.map((m) => <th key={m} className="px-2 py-2 text-center font-medium">{t(`nav.${m}`)}</th>)}
                <th className="px-3 py-2 font-medium">{t("access.home")}</th>
                <th className="px-3 py-2 font-medium">{t("access.active")}</th>
                <th className="px-3 py-2 font-medium">{t("access.adminCol")}</th>
              </tr>
            </thead>
            <tbody>
              {q.data?.staffs.map((s) => <Row key={s.id} staff={s} own={s.id === me?.id} modules={modules} onChanged={refresh} />)}
            </tbody>
          </table>
        </div>
      </section>
      <NewPerson modules={modules} onCreated={refresh} />
    </div>
  );
}

function Row({ staff, own, modules, onChanged }: { staff: StaffRow; own: boolean; modules: string[]; onChanged: () => void }) {
  const { t } = useT();
  const save = useMutation({ mutationFn: (b: Parameters<typeof updateStaff>[1]) => updateStaff(staff.id, b), onSuccess: onChanged });
  const ownLocked = own ? t("access.ownLocked") : undefined;
  const toggle = (m: string) => {
    const next = staff.modules.includes(m) ? staff.modules.filter((x) => x !== m) : [...staff.modules, m];
    // A home outside the modules would be refused: it follows the first module left.
    const home = staff.home_module && next.includes(staff.home_module) ? staff.home_module : next[0] ?? null;
    save.mutate({ modules: next, home_module: home });
  };
  return (
    <tr className="border-t border-slate-100">
      <td className="px-5 py-2">
        <span className="font-medium text-slate-800">{staff.name || staff.email}</span>
        {staff.is_admin && <span className="ml-2 rounded bg-slate-900 px-1.5 py-0.5 text-xs text-white">{t("access.admin")}</span>}
        <span className="block text-xs text-slate-400">{staff.email}</span>
        {save.error && <span className="block text-xs text-red-700">{(save.error as Error).message}</span>}
        <NewPassword staff={staff} />
      </td>
      {modules.map((m) => (
        <td key={m} className="px-2 py-2 text-center">
          <input type="checkbox" aria-label={`${staff.email} ${m}`} disabled={staff.is_admin || save.isPending}
            checked={staff.is_admin || staff.modules.includes(m)} onChange={() => toggle(m)} />
          {/* His viewer level: the module is seen, and nothing in it is written. */}
          {!staff.is_admin && staff.modules.includes(m) && (
            <label className="mt-0.5 flex items-center justify-center gap-1 text-[11px] text-slate-500">
              <input type="checkbox" disabled={save.isPending} checked={staff.read_only_modules.includes(m)}
                onChange={() => save.mutate({ read_only_modules: staff.read_only_modules.includes(m)
                  ? staff.read_only_modules.filter((x) => x !== m) : [...staff.read_only_modules, m] })} />
              {t("access.readOnly")}
            </label>
          )}
        </td>
      ))}
      <td className="px-3 py-2">
        <select value={staff.home_module ?? ""} disabled={save.isPending}
          onChange={(e) => save.mutate({ home_module: e.target.value || null })}
          className="rounded border border-slate-300 px-2 py-1 text-sm">
          <option value="">—</option>
          {staff.uses.map((m) => <option key={m} value={m}>{t(`nav.${m}`)}</option>)}
        </select>
      </td>
      <td className="px-3 py-2">
        <input type="checkbox" aria-label={`${staff.email} ${t("access.active")}`} checked={staff.active} title={ownLocked}
          disabled={own || save.isPending} onChange={() => save.mutate({ active: !staff.active })} />
      </td>
      <td className="px-3 py-2">
        <input type="checkbox" aria-label={`${staff.email} ${t("access.adminCol")}`} checked={staff.is_admin} title={ownLocked}
          disabled={own || save.isPending} onChange={() => save.mutate({ is_admin: !staff.is_admin })} />
      </td>
    </tr>
  );
}

// A forgotten password is replaced here by an admin — this app sends no email, so there is no reset link.
function NewPassword({ staff }: { staff: StaffRow }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const set = useMutation({
    mutationFn: () => updateStaff(staff.id, { password }),
    onSuccess: () => { setPassword(""); setOpen(false); },
  });
  if (!open) {
    return (
      <span className="block">
        <button type="button" onClick={() => { set.reset(); setOpen(true); }} className="text-xs text-slate-500 underline">{t("access.setPassword")}</button>
        {set.isSuccess && <span className="ml-2 text-xs text-emerald-700">{t("access.passwordChanged")}</span>}
      </span>
    );
  }
  return (
    <span className="mt-1 flex flex-wrap items-center gap-1.5">
      <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="new-password"
        aria-label={`${staff.email} ${t("access.setPassword")}`} placeholder={t("access.password")}
        className="w-52 rounded border border-slate-300 px-2 py-1 text-xs" />
      <button type="button" onClick={() => set.mutate()} disabled={password.length < 6 || set.isPending}
        className="rounded bg-slate-900 px-2 py-1 text-xs font-semibold text-white disabled:opacity-40">{t("access.savePassword")}</button>
      <button type="button" onClick={() => { setPassword(""); setOpen(false); }} className="text-xs text-slate-500 underline">{t("access.cancel")}</button>
      {set.error && <span className="block w-full text-xs text-red-700">{(set.error as Error).message}</span>}
    </span>
  );
}

// A new account with the password the admin gives it — this app sends no email.
function NewPerson({ modules, onCreated }: { modules: string[]; onCreated: () => void }) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const create = useMutation({
    mutationFn: () => createStaff({ name, email, password, modules: chosen, home_module: chosen[0] ?? null }),
    onSuccess: () => { setName(""); setEmail(""); setPassword(""); setChosen([]); onCreated(); },
  });
  return (
    <section className="rounded-xl border border-slate-200 bg-white px-5 py-4">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{t("access.new")}</h2>
      <div className="flex flex-wrap gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("access.name")} className="w-44 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@myotaku.ch" className="w-60 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="new-password"
          placeholder={t("access.password")} className="w-52 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-700">
        {modules.map((m) => (
          <label key={m} className="flex items-center gap-1.5">
            <input type="checkbox" checked={chosen.includes(m)}
              onChange={() => setChosen(chosen.includes(m) ? chosen.filter((x) => x !== m) : [...chosen, m])} />
            {t(`nav.${m}`)}
          </label>
        ))}
      </div>
      <p className="mt-1 text-xs text-slate-500">{t("access.homeFirst")}</p>
      <button onClick={() => create.mutate()} disabled={!email.trim() || password.length < 6 || chosen.length === 0 || create.isPending}
        className="mt-2 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">{t("access.create")}</button>
      {create.error && <p className="mt-2 text-sm text-red-700">{(create.error as Error).message}</p>}
    </section>
  );
}
